import { useGetTasksByCourseModule, getGetTasksByCourseModuleQueryKey } from '../../api/endpoints/tasks/tasks';
/**
 * Course-aware wrapper around task-related orval hooks specifically for students.
 * Handles tasks in the context of a course-module combination, managing course-specific
 * task configurations and submissions.
 *
 * @param {Object} params
 * @param {string} params.courseId - The course ID to fetch tasks for
 * @param {string} params.moduleId - The module ID to fetch tasks for
 * @param {string[]} [params.includes=['submissions', 'resources']] - Additional data to include in the response
 * @param {Object} [params.options] - Additional options for the query
 * @param {boolean} [params.options.preventAutoRefetch=false] - If true, prevents auto refetching on window focus and reconnect
 */
export const useModuleData = (params) => {
  const {
    courseId,
    moduleId,
    includes = ['submissions', 'resources'],
    options = {},
  } = params || {};

  const isForbiddenError = (err) => err?.response?.status === 403;

  // console.log('useModuleData called with:', { courseId, moduleId });

  const includesParam = includes.length > 0 ? { include: includes.join(',') } : undefined;
  const queryKey = getGetTasksByCourseModuleQueryKey(courseId, moduleId, includesParam);
  const {
    data: rawData,
    isLoading,
    isError,
    error,
    refetch,
  } = useGetTasksByCourseModule(
    courseId,
    moduleId,
    includesParam,
    {
      query: {
        staleTime: 1000 * 60 * 15, // 15 minutes
        retry: (failureCount, err) => !isForbiddenError(err),
        refetchOnWindowFocus: (query) => (isForbiddenError(query.state.error) ? false : !options.preventAutoRefetch),
        refetchOnReconnect: (query) => (isForbiddenError(query.state.error) ? false : !options.preventAutoRefetch),
        enabled: !!(moduleId && courseId),
        structuralSharing: true, // Preserve reference equality for unchanged parts of the data
      },
    },
  );

  return {
    tasks: rawData?.data?.data || [],
    isLoading: isLoading && !!(moduleId && courseId),
    isError,
    error,
    rawData, // Expose rawData for metadata access
    refetch, // Expose refetch for manual refreshes
    queryKey,
  };
};

export default useModuleData;
